import {Injectable} from '@angular/core';
import {Decimal} from 'decimal.js';
import * as _ from 'lodash-es';
import {createNumberFormat} from '../../common/formatters';
import {createVolume24hFormatter} from '../../components/order-qty-multi-currency';
import {
    createDisplayDecimal,
    DisplayDecimal,
    DisplayDecimalWithCurrency,
    formatPercents,
    formatPrice,
    formatQuantity,
    SymbolDecimalsService,
    SymbolFormats,
    ValueFormatter,
    ValueFormatterWithCurrency
} from '../../components/symbol-decimals';
import {Side} from '../../enums/side';
import {Security} from '../../models/security';
import {SecurityKey} from '../../models/security-key';
import {Quote} from '../components/quotes-streaming/quote';

export interface FreezableDisplayQuoteSideInfo {
    security: Security; // Used in order to open Trade widget by clicking on the cell.

    price: FreezableDisplayQuoteAnimationValue;
    volume: FreezableDisplayVolumeValue;
    side: Side;
    isFrozen?: boolean;

    // Used to prevent unwanted unfreeze while moving mouse between price and volume of the same side.
    pendingUnfreeze?: boolean;
}

export interface DisplayQuote {
    quote: Quote;

    priceFormatter: (value: number) => DisplayDecimal;
    qtyFormatter: (value: number) => DisplayDecimal;
    volume24hFormatter: (value: number) => DisplayDecimalWithCurrency;

    bid: FreezableDisplayQuoteSideInfo;
    ask: FreezableDisplayQuoteSideInfo;
    tradePrice: DisplayQuoteTradeValue;
    open: DisplayDecimal;
    fundingRate: DisplayDecimal;
    high: DisplayDecimal;
    low: DisplayDecimal;
    volume: DisplayDecimalWithCurrency;
}

enum AnimationIncrementType {
    NONE,
    UP,
    DOWN
}

export interface DisplayQuoteAnimationValue {
    value: number;
    displayValue: DisplayDecimal;
    incrementType: AnimationIncrementType;
    flashIndex: number;
    animationClasses: string;

    // for 24h changes classes
    animationClass24: string;
    incrementType24: AnimationIncrementType;
}

export interface DisplayQuoteTradeValue extends DisplayQuoteAnimationValue {
    changeValue?: number;
    displayChangeValue?: DisplayDecimal;
    displayChangePercent?: DisplayDecimal;
}

interface DisplayVolumeValue {
    value: number;
    displayValue: DisplayDecimal;
}

interface Freezable<T extends {value: any} = {value: any}> {
    value: T['value'];
    freezeMetadata?: {
        frozen: Partial<T>;
        live: Partial<T>;
    };
}

export interface FreezableDisplayQuoteAnimationValue extends DisplayQuoteAnimationValue,
    Freezable<FreezableDisplayQuoteAnimationValue> {}

export interface FreezableDisplayVolumeValue extends DisplayVolumeValue,
    Freezable<DisplayVolumeValue> {}

const ANIMATION_INCREMENT_CLASSES = {
    [AnimationIncrementType.NONE]: '',
    [AnimationIncrementType.UP]:   '-up',
    [AnimationIncrementType.DOWN]: '-down'
};

const ANIMATION_FLASH_CLASS_COUNT = 5; // Should be less or equal to the related value in the styles.
const ANIMATION_FLASH_CLASSES = _.times(ANIMATION_FLASH_CLASS_COUNT, index => `-flash-${index}`);

@Injectable()
export class DisplayQuoteFactory {

    private static readonly fundingRateFormat = createNumberFormat({
        isDecimalRequired: true,
        decimalCount:      4
    });

    private static getChangePercentValue(value: number, total: number): number {
        return !value ? null : new Decimal(value).minus(total).dividedBy(total).toNumber();
    }

    private static getEmptyFormatter(): ValueFormatter {
        return value => ({
            value:         undefined,
            originalValue: value,
            class:         ''
        });
    }

    private static isNotFullSecurity(security: SecurityKey | Security): security is Security {

        // Check if it's empty quote with securityKey in place of security
        return (security as Security).priceScale === undefined;
    }

    constructor(
        private symbolDecimalsService: SymbolDecimalsService
    ) {}

    public createDisplayQuote(quote: Quote): DisplayQuote {
        const isFullSecurity = DisplayQuoteFactory.isNotFullSecurity(quote.security);

        const {priceFormatter, qtyFormatter, volume24hFormatter} = this.createFormatters(quote.security);

        const bid: FreezableDisplayQuoteSideInfo = {
            security: quote.security,
            price: this.createAnimationValue(quote.bid, priceFormatter),
            volume: this.createVolumeValue(quote.bidVolume, qtyFormatter),
            side: Side.BUY
        };
        const ask: FreezableDisplayQuoteSideInfo = {
            security: quote.security,
            price: this.createAnimationValue(quote.ask, priceFormatter),
            volume: this.createVolumeValue(quote.askVolume, qtyFormatter),
            side: Side.SELL
        };

        const tradePrice: DisplayQuoteAnimationValue = this.createAnimationValue(quote.trade, priceFormatter);
        this.updateTradeValue(tradePrice, quote.trade, priceFormatter, quote.open);

        return {
            quote,

            // Don't store default formatters
            priceFormatter:     isFullSecurity ? undefined : priceFormatter,
            qtyFormatter:       isFullSecurity ? undefined : qtyFormatter,
            volume24hFormatter: isFullSecurity ? undefined : volume24hFormatter,

            bid,
            ask,
            tradePrice,
            high:        priceFormatter(quote.high),
            low:         priceFormatter(quote.low),
            open:        priceFormatter(quote.open),
            fundingRate: createDisplayDecimal(quote.fundingRate, DisplayQuoteFactory.fundingRateFormat),
            volume:      volume24hFormatter(quote.volume)
        };
    }

    public updateDisplayQuote(displayQuote: DisplayQuote, quote: Quote): void {
        const {security} = quote;
        const {priceFormatter, qtyFormatter, volume24hFormatter} = displayQuote.priceFormatter
            ? displayQuote
            : this.createFormatters(security);

        this.updateAnimationValue(displayQuote.bid.price, quote.bid, priceFormatter);
        this.updateVolumeValue(displayQuote.bid.volume, quote.bidVolume, qtyFormatter);

        this.updateAnimationValue(displayQuote.ask.price, quote.ask, priceFormatter);
        this.updateVolumeValue(displayQuote.ask.volume, quote.askVolume, qtyFormatter);

        this.updateTradeValue(displayQuote.tradePrice, quote.trade, priceFormatter, quote.open);

        Object.assign(displayQuote, {
            priceFormatter,
            qtyFormatter,
            volume24hFormatter,
            high:        priceFormatter(quote.high),
            low:         priceFormatter(quote.low),
            open:        priceFormatter(quote.open),
            fundingRate: createDisplayDecimal(quote.fundingRate, DisplayQuoteFactory.fundingRateFormat),
            volume:      volume24hFormatter(quote.volume)
        } as Partial<DisplayQuote>);

        displayQuote.quote = quote;

        /**
         * Previous security might be empty.
         */
        displayQuote.bid.security = security;
        displayQuote.ask.security = security;
    }

    public toggleSideInfoFreezing(isFreezing: boolean, sideInfo: FreezableDisplayQuoteSideInfo): void {
        if (sideInfo && !!sideInfo.isFrozen !== isFreezing) {
            this.toggleFreezableState(
                isFreezing,
                sideInfo.price,

                // Don't freeze the value property since it's used in the live animations calculation.
                ['displayValue', 'animationClasses'],
                {
                    animationClasses: ''
                }
            );
            this.toggleFreezableState(
                isFreezing,
                sideInfo.volume,
                ['value', 'displayValue']
            );

            sideInfo.isFrozen = isFreezing;
        }
    }

    private createFormatters(
        security: Security
    ): Pick<DisplayQuote, 'priceFormatter' | 'qtyFormatter' | 'volume24hFormatter'> {
        let priceFormatter: ValueFormatter;
        let qtyFormatter: ValueFormatter;
        let volume24hFormatter: ValueFormatterWithCurrency;

        const isFullSecurity = !DisplayQuoteFactory.isNotFullSecurity(security);

        if (isFullSecurity) {
            const symbolFormats: SymbolFormats = this.symbolDecimalsService.getSecurityFormats(security);

            priceFormatter = value => formatPrice(value, symbolFormats);
            qtyFormatter = value => formatQuantity(value, symbolFormats);
            volume24hFormatter = createVolume24hFormatter(security, symbolFormats);
        } else {
            priceFormatter = DisplayQuoteFactory.getEmptyFormatter();
            qtyFormatter = DisplayQuoteFactory.getEmptyFormatter();
            volume24hFormatter = DisplayQuoteFactory.getEmptyFormatter() as ValueFormatterWithCurrency;
        }

        return {
            priceFormatter,
            qtyFormatter,
            volume24hFormatter
        };
    }

    private toggleFreezableState<T extends {value: any} = {value: any}>(
        isFreezing: boolean,
        obj: T,
        keysToFreeze: Array<keyof T>,
        valuesToFreezeWith?: Partial<T>
    ): Freezable<T> {
        const freezable: Freezable = obj as Freezable<T>;

        if (isFreezing && !freezable.freezeMetadata) {
            const currentValues: Partial<T> = _.pick(obj, keysToFreeze);

            freezable.freezeMetadata = {
                frozen: {
                    value: obj.value,
                    ...currentValues as object
                },
                live: currentValues
            };

            const propertyDefs: Partial<{[key in keyof T]: PropertyDescriptor}> = {};

            keysToFreeze.forEach((key: keyof T) => {
                const def: PropertyDescriptor = propertyDefs[key] = {
                    configurable: true,
                    enumerable: true,
                    set: value => (freezable.freezeMetadata.live as T)[key] = value
                };

                def.get = valuesToFreezeWith && valuesToFreezeWith.hasOwnProperty(key)
                    ? () => valuesToFreezeWith[key]
                    : () => (freezable.freezeMetadata.frozen as T)[key];
            });

            Object.defineProperties(freezable, propertyDefs);

        } else if (!isFreezing && freezable.freezeMetadata) {
            keysToFreeze.forEach((key: keyof T) => {
                delete obj[key];
                obj[key] = (freezable.freezeMetadata.live as T)[key];
            });

            delete freezable.freezeMetadata;
        }

        return obj;
    }

    private createVolumeValue(value: number, formatter: ValueFormatter): DisplayVolumeValue {
        return {
            value,
            displayValue: formatter(value)
        };
    }

    private updateVolumeValue(
        volumeValue: DisplayVolumeValue,
        newValue: number,
        formatter: ValueFormatter
    ): void {
        volumeValue.value = newValue;
        volumeValue.displayValue = formatter(newValue);
    }

    private createAnimationValue(value: number, formatter: ValueFormatter): DisplayQuoteAnimationValue {
        return {
            value,
            displayValue: formatter(value),
            incrementType: AnimationIncrementType.NONE,
            flashIndex: 0,
            animationClasses: '',
            incrementType24: AnimationIncrementType.NONE,
            animationClass24: ''
        };
    }

    private getAnimationIncrementType(
        oldValue: number,
        newValue: number,
        oldAnimationIncrementType: AnimationIncrementType
    ): AnimationIncrementType {
        const isValueEmpty = (value: number) => !value && value !== 0;
        const isOldValueEmpty = isValueEmpty(oldValue);
        const isNewValueEmpty = isValueEmpty(newValue);

        return !isOldValueEmpty && !isNewValueEmpty && oldValue !== newValue
            ? oldValue < newValue
                ? AnimationIncrementType.UP
                : AnimationIncrementType.DOWN
            : isOldValueEmpty || isNewValueEmpty
                ? AnimationIncrementType.NONE
                : oldAnimationIncrementType;
    }

    private updateAnimationValue(
        animationValue: DisplayQuoteAnimationValue,
        newValue: number,
        formatter: ValueFormatter,
        openPrice?: number
    ): void {
        const oldValue = animationValue.value || openPrice;
        const oldIncrementType = animationValue.incrementType;

        const incrementType = this.getAnimationIncrementType(oldValue, newValue, oldIncrementType);

        animationValue.value = newValue;
        animationValue.displayValue = formatter(newValue);
        animationValue.incrementType = incrementType;

        if (incrementType !== AnimationIncrementType.NONE) {
            const incrementClass = ANIMATION_INCREMENT_CLASSES[incrementType];
            const flashIndex = oldValue !== newValue
                ? incrementType === oldIncrementType
                    ? (animationValue.flashIndex + 1) % ANIMATION_FLASH_CLASS_COUNT
                    : 0
                : animationValue.flashIndex;
            const flashClass = ANIMATION_FLASH_CLASSES[flashIndex];

            animationValue.flashIndex = flashIndex;
            animationValue.animationClasses = [incrementClass, flashClass].join(' ');
        } else {
            animationValue.flashIndex = 0;
            animationValue.animationClasses = '';
        }

        /**
         * css class for 24h changes
         */
        const oldIncrementType24 = animationValue.incrementType24;
        const incrementType24 = this.getAnimationIncrementType(openPrice, newValue, oldIncrementType24);
        animationValue.incrementType24 = incrementType24;
        animationValue.animationClass24 = incrementType24 !== AnimationIncrementType.NONE
            ? ANIMATION_INCREMENT_CLASSES[incrementType24]
            : '';
    }

    private updateTradeValue(
        tradeValue: DisplayQuoteTradeValue,
        newValue: number,
        formatter: ValueFormatter,
        openPrice: number
    ): void {
        const isValueEmpty = (value: number) => !value && value !== 0;
        const isNewValueEmpty = isValueEmpty(newValue);
        const isOpenPriceEmpty = isValueEmpty(openPrice);

        if (!isNewValueEmpty && !isOpenPriceEmpty) {
            tradeValue.changeValue = newValue - openPrice;
            tradeValue.displayChangeValue = formatter(tradeValue.changeValue);
            tradeValue.displayChangePercent = formatPercents(
                DisplayQuoteFactory.getChangePercentValue(newValue, openPrice)
            );

            if (tradeValue.changeValue > 0) {
                tradeValue.displayChangeValue.value = `+${tradeValue.displayChangeValue.value}`;
                tradeValue.displayChangePercent.value = `+${tradeValue.displayChangePercent.value}`;
            }
        } else {
            tradeValue.changeValue = null;
            tradeValue.displayChangeValue = null;
            tradeValue.displayChangePercent = null;
        }

        this.updateAnimationValue(tradeValue, newValue, formatter, openPrice);
    }

}
